// Ambassador Performance · admin-only referral tracking (V1)
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { PayoutCell, PayoutHistory } from "../components/AmbassadorPayouts";

const NAVY = "#1B2340";
const GOLD = "#B8913A";
const card = { background: "#fff", border: "1px solid #E7E1D3", borderRadius: 12, padding: 18 };
const input = { padding: "8px 10px", border: "1px solid #D9D2C2", borderRadius: 8, fontSize: 13, fontFamily: "inherit" };
const btn = { padding: "7px 14px", borderRadius: 999, border: `1px solid ${GOLD}`, background: "transparent", color: GOLD, cursor: "pointer", fontSize: 12 };
const btnSolid = { ...btn, background: GOLD, color: "#fff" };

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(n % 1 ? 2 : 0)}`);
const pct = (n) => (n == null ? "—" : `${n}%`);
const date = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Stat({ label, value, testid }) {
  return (
    <div style={card} data-testid={testid}>
      <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#7A7466" }}>{label}</div>
      <div style={{ fontSize: 26, marginTop: 6 }}>{value}</div>
    </div>
  );
}

const EMPTY = { name: "", email: "", code: "", monthly_comp: 100, bonus_amount: 50, monthly_target: 5 };

function CreateForm({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, monthly_comp: Number(f.monthly_comp), bonus_amount: Number(f.bonus_amount), monthly_target: Number(f.monthly_target) };
      if (!body.code) delete body.code;
      if (!body.email) delete body.email;
      await api.post("/admin/ambassadors", body);
      toast.success("Ambassador created");
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create ambassador");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} style={{ ...card, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }} data-testid="ambassador-create-form">
      <input style={input} placeholder="Name *" value={f.name} onChange={set("name")} required data-testid="ambassador-name-input" />
      <input style={input} placeholder="Email" type="email" value={f.email} onChange={set("email")} data-testid="ambassador-email-input" />
      <input style={{ ...input, width: 120 }} placeholder="Code (auto)" value={f.code} onChange={set("code")} data-testid="ambassador-code-input" />
      <label style={{ fontSize: 11 }}>Monthly $<br /><input style={{ ...input, width: 80 }} type="number" min="0" value={f.monthly_comp} onChange={set("monthly_comp")} data-testid="ambassador-comp-input" /></label>
      <label style={{ fontSize: 11 }}>Bonus $<br /><input style={{ ...input, width: 80 }} type="number" min="0" value={f.bonus_amount} onChange={set("bonus_amount")} data-testid="ambassador-bonus-input" /></label>
      <label style={{ fontSize: 11 }}>Monthly target<br /><input style={{ ...input, width: 80 }} type="number" min="0" value={f.monthly_target} onChange={set("monthly_target")} data-testid="ambassador-target-input" /></label>
      <button type="submit" style={btnSolid} disabled={busy} data-testid="ambassador-create-submit">{busy ? "Creating…" : "Add ambassador"}</button>
    </form>
  );
}

function Referrals({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/admin/ambassadors/${id}/referrals`).then((r) => setRows(r.data.referrals)).catch(() => setRows([]));
  }, [id]);
  if (!rows) return <div style={{ fontSize: 12, padding: 8 }}>Loading…</div>;
  if (!rows.length) return <div style={{ fontSize: 12, padding: 8 }} data-testid={`ambassador-referrals-empty-${id}`}>No completed registrations yet.</div>;
  return (
    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid={`ambassador-referrals-${id}`}>
      <thead><tr style={{ textAlign: "left", color: "#7A7466" }}><th>Member</th><th>Email</th><th>Method</th><th>Registered</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.referral_id} data-testid={`ambassador-referral-row-${r.user_id}`}>
            <td>{r.member_name || "—"}</td><td>{r.email || "—"}</td><td>{r.signup_method}</td><td>{date(r.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TargetEditor({ a, onSaved }) {
  const [v, setV] = useState(a.monthly_target);
  const save = async () => {
    try { await api.patch(`/admin/ambassadors/${a.ambassador_id}`, { monthly_target: Number(v) }); toast.success("Target updated"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <input style={{ ...input, width: 52, padding: "4px 6px" }} type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} data-testid={`ambassador-target-edit-${a.code}`} />
      {String(v) !== String(a.monthly_target) && <button style={{ ...btn, marginLeft: 4, padding: "3px 8px" }} onClick={save} data-testid={`ambassador-target-save-${a.code}`}>Save</button>}
    </span>
  );
}

function Row({ a, month, onChanged }) {
  const [open, setOpen] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(a.link); toast.success("Link copied"); }
    catch { toast.error(a.link); }
  };
  const toggle = async () => {
    try { await api.patch(`/admin/ambassadors/${a.ambassador_id}`, { active: !a.active }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const td = { padding: "10px 8px", borderTop: "1px solid #EEE8DA", verticalAlign: "top" };
  return (
    <>
      <tr data-testid={`ambassador-row-${a.code}`} style={{ opacity: a.active ? 1 : 0.55 }}>
        <td style={td}><strong>{a.name}</strong><div style={{ fontSize: 11, color: "#7A7466" }}>{a.email || ""}</div></td>
        <td style={td}>
          <code data-testid={`ambassador-code-${a.code}`}>{a.code}</code>
          <div style={{ fontSize: 11, color: "#7A7466", maxWidth: 220, wordBreak: "break-all" }} data-testid={`ambassador-link-${a.code}`}>{a.link}</div>
        </td>
        <td style={td} data-testid={`ambassador-clicks-${a.code}`}>{a.unique_clicks_month}<div style={{ fontSize: 11, color: "#7A7466" }}>{a.unique_clicks_total} all · {a.clicks_total} raw</div></td>
        <td style={td} data-testid={`ambassador-registrations-${a.code}`}><strong>{a.registrations_month}</strong><div style={{ fontSize: 11, color: "#7A7466" }}>{a.registrations_total} all-time</div></td>
        <td style={td} data-testid={`ambassador-conversion-${a.code}`}>{pct(a.conversion_rate_month)}<div style={{ fontSize: 11, color: "#7A7466" }}>{pct(a.conversion_rate_total)} all</div></td>
        <td style={td} data-testid={`ambassador-dates-${a.code}`}><div style={{ fontSize: 11 }}>First {date(a.first_referral_at)}</div><div style={{ fontSize: 11 }}>Last {date(a.last_referral_at)}</div></td>
        <td style={td}><TargetEditor key={a.monthly_target} a={a} onSaved={onChanged} /></td>
        <td style={td} data-testid={`ambassador-eligible-${a.code}`}>
          {a.incentive_eligible
            ? <span style={{ color: "#2E7D4F", fontWeight: 600 }}>Eligible +{money(a.bonus_amount)}</span>
            : <span style={{ color: "#7A7466" }}>{a.remaining_to_target} to go</span>}
        </td>
        <td style={td} data-testid={`ambassador-retention-${a.code}`}>
          {pct(a.retention_rate_30d)}
          <div style={{ fontSize: 11, color: "#7A7466" }}>{a.retained_30d} of {a.retention_eligible} past day 30</div>
        </td>
        <td style={td} data-testid={`ambassador-cost-${a.code}`}>{money(a.month_compensation)}<div style={{ fontSize: 11, color: "#7A7466" }}>{money(a.cost_per_member_month)} / member</div></td>
        <td style={td}><PayoutCell a={a} month={month} onChanged={onChanged} /></td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          <button style={btn} onClick={copy} data-testid={`ambassador-copy-${a.code}`}>Copy link</button>{" "}
          <button style={btn} onClick={toggle} data-testid={`ambassador-toggle-${a.code}`}>{a.active ? "Deactivate" : "Activate"}</button>{" "}
          <button style={btn} onClick={() => setOpen(!open)} data-testid={`ambassador-review-${a.code}`}>{open ? "Hide" : "Review"}</button>
        </td>
      </tr>
      {open && <tr><td colSpan={12} style={{ padding: "4px 8px 14px", background: "#FBFAF6" }}><Referrals id={a.ambassador_id} /><PayoutHistory id={a.ambassador_id} version={JSON.stringify(a.payout)} /></td></tr>}
    </>
  );
}

export default function AdminAmbassadors() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/ambassadors", { params: { month } }); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load ambassadors"); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const [sending, setSending] = useState(false);
  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/ambassadors/export.csv", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = `ambassador-payouts-${month}.csv`;
      el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const sendRecap = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/ambassadors/recap/send-test", { month });
      if (r.data.ok) toast.success(`Recap for ${r.data.month} sent to ${r.data.recipient}`);
      else toast.error(r.data.error || "Recap email failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Recap email failed"); }
    finally { setSending(false); }
  };

  const t = data?.totals || {};
  return (
    <div data-testid="admin-ambassadors" style={{ background: "#FAF8F3", minHeight: "100vh", padding: "32px 40px", fontFamily: "Georgia, serif", color: NAVY }}>
      <Toaster richColors position="top-center" />
      <Link to="/admin" style={{ color: GOLD, fontSize: 13 }} data-testid="ambassadors-back-link">← Admin</Link>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16, margin: "12px 0 24px", flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 34, margin: 0, fontWeight: 400 }}>Ambassador Performance</h1>
          <p style={{ margin: "6px 0 0", color: "#7A7466", fontSize: 14 }}>Completed registrations per ambassador. Payouts are tracked here only — never processed automatically.</p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button style={btn} onClick={exportCsv} data-testid="ambassadors-export-csv">Export CSV</button>
          <button style={btn} onClick={sendRecap} disabled={sending} data-testid="ambassadors-send-recap">{sending ? "Sending…" : "Send test recap"}</button>
        </div>
        <label style={{ fontSize: 12 }}>Month&nbsp;
          <input type="month" style={input} value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} data-testid="ambassadors-month-input" />
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginBottom: 20 }}>
        <Stat label="Active ambassadors" value={t.ambassadors_active ?? "—"} testid="ambassadors-total-active" />
        <Stat label="Registrations (month)" value={t.registrations_month ?? "—"} testid="ambassadors-total-registrations" />
        <Stat label="Unique clicks (month)" value={t.unique_clicks_month ?? "—"} testid="ambassadors-total-clicks" />
        <Stat label="Compensation (month)" value={money(t.compensation_month)} testid="ambassadors-total-comp" />
        <Stat label="Cost per member" value={money(t.cost_per_member_month)} testid="ambassadors-total-cpa" />
      </div>
      <CreateForm onCreated={load} />
      <div style={{ ...card, marginTop: 16, overflowX: "auto", padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="ambassadors-table">
          <thead>
            <tr style={{ textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: "#7A7466" }}>
              {["Ambassador", "Code / link", "Clicks", "Registrations", "Conversion", "Referral dates", "Target", "Incentive", "30-day retention", "Cost", "Payout", ""].map((h) => (
                <th key={h} style={{ padding: "12px 8px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.ambassadors || []).map((a) => <Row key={a.ambassador_id} a={a} month={data.month} onChanged={load} />)}
            {data && !data.ambassadors.length && (
              <tr><td colSpan={12} style={{ padding: 20, color: "#7A7466" }} data-testid="ambassadors-empty">No ambassadors yet. Add one above to generate a referral link.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
